import type {
  ControlType,
  FieldData,
  FieldType,
  InputConfig,
  PrimitiveValueType,
  ValueType,
} from 'components/types';
import {
  ComboBoxMultiple,
  type ComboBoxMultipleProps,
  ComboBoxSingle,
  type ComboBoxSingleProps,
  Custom,
  type CustomProps,
  DateComponent,
  type DateProps,
  FilePickerDialog,
  type FilePickerDialogProps,
  MultiFile,
  type MultiFileProps,
  NumberBox,
  type NumberBoxProps,
  PhoneNumberInput,
  type PhoneNumberInputProps,
  RadioGroup,
  type RadioGroupProps,
  TextBox,
  type TextBoxProps,
  TextDisplay,
  type TextDisplayProps,
  ToggleButton,
  type ToggleButtonProps,
} from 'design-system/components';
import type React from 'react';
import type { ReactElement } from 'react';

type SelectiveControlProps<TData extends FieldData<TData>> = {
  value: FieldType | ValueType[];
  onChange: (newValue: FieldType | ValueType[]) => void;
  disabled?: boolean;
  config: InputConfig<TData>;
  errors?: string;
};

interface ComponentProps {
  'toggle-button': ToggleButtonProps<PrimitiveValueType>;
  'text-box': TextBoxProps;
  'number-box': NumberBoxProps;
  'radio-group': RadioGroupProps<PrimitiveValueType>;
  'combobox-single': ComboBoxSingleProps<PrimitiveValueType>;
  'combobox-multiple': ComboBoxMultipleProps<PrimitiveValueType>;
  custom: CustomProps;
  'multi-file': MultiFileProps;
  'date-component': DateProps;
  'file-picker-dialog': FilePickerDialogProps;
  'text-display': TextDisplayProps;
  'phone-number': PhoneNumberInputProps;
}

const components: Record<ControlType, React.FC<ComponentProps[ControlType]>> = {
  'toggle-button': (props) => (
    <ToggleButton {...(props as ToggleButtonProps<PrimitiveValueType>)} />
  ),
  'text-box': (props) => <TextBox {...(props as TextBoxProps)} />,
  'number-box': (props) => <NumberBox {...(props as NumberBoxProps)} />,
  'radio-group': (props) => (
    <RadioGroup {...(props as RadioGroupProps<PrimitiveValueType>)} />
  ),
  'combobox-single': (props) => (
    <ComboBoxSingle {...(props as ComboBoxSingleProps<PrimitiveValueType>)} />
  ),
  'combobox-multiple': (props) => (
    <ComboBoxMultiple
      {...(props as ComboBoxMultipleProps<PrimitiveValueType>)}
    />
  ),
  custom: (props) => <Custom {...(props as CustomProps)} />,
  'multi-file': (props) => <MultiFile {...(props as MultiFileProps)} />,
  'date-component': (props) => <DateComponent {...(props as DateProps)} />,
  'file-picker-dialog': (props) => (
    <FilePickerDialog {...(props as FilePickerDialogProps)} />
  ),
  'text-display': (props) => <TextDisplay {...(props as TextDisplayProps)} />,
  'phone-number': (props) => (
    <PhoneNumberInput {...(props as PhoneNumberInputProps)} />
  ),
};

export const SelectiveControl = <TData extends FieldData<TData>>(
  props: SelectiveControlProps<TData>,
): ReactElement => {
  const Component = components[props.config.type];
  const { key, ...configProps } = props.config;
  const componentProps: ComponentProps[typeof props.config.type] = {
    // allow the individual component config to override the disabled value if required
    isDisabled: props.disabled,
    ...configProps,
    errors: props.errors,
    onChange: props.onChange,
    value: props.value,
  } as ComponentProps[typeof props.config.type];

  return <Component {...componentProps} />;
};
